import { type ReactNode, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, KeyRound, PenLine, Settings, Mail, Sun, Moon, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n/useTranslations';
import { useTheme } from '@/components/theme-provider';
import { useNavigation } from '@/hooks/use-navigation';
import { useLogout } from '@/hooks/use-logout';
import { useAuthStore } from '@/stores/auth-store';
import { useMessageStore } from '@/stores/message-store';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useShowTryGuidedButton } from '@/hooks/use-guided-ui-mode';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { UserRole } from '@comp-sys/shared';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { MobileSidebar } from '@/components/layout/mobile-sidebar';
import { BrandLogo } from '@/components/shared/brand-logo';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';
import { RoleSwitcherMenuItems } from '@/components/auth/role-switcher';
import { ChangePasswordDialog } from '@/components/users/change-password-dialog';
import { ChangeNameDialog } from '@/components/users/change-name-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** 软触感阴影 —— 大圆角浮岛/基座的柔光 elevation（theme-aware via token 叠加）。 */
const PLINTH_SHADOW = 'shadow-[0_20px_60px_-15px_rgba(0,0,0,0.18),0_8px_24px_-12px_rgba(0,0,0,0.12)]';
const DOCK_SHADOW = 'shadow-[0_12px_40px_-8px_rgba(0,0,0,0.22)]';

/**
 * Soft 家族 shell（neumorphism 主题）——「触感工坊」。
 *
 * 摒弃侧栏与顶栏，采用触感浮岛编排：
 * - 底部居中浮岛 dock 导航（macOS 式，图标悬停放大），最独特的导航位置
 * - 内容坐在超大圆角「基座 plinth」上，柔光 elevation，触感浮起
 * - 顶部极薄浮岛药丸（logo + 工具），不抢占内容
 * - 柔软过渡（淡入 + 轻微缩放）
 *
 * 原子控件的凹凸阴影由 data-skin="soft" CSS 处理；本 shell 只负责浮岛/基座的几何编排。
 */
export function SoftShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const user = useAuthStore((state) => state.user);
  const { navItems } = useNavigation();
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';
  const t = useTranslations('nav');

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const isActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'));

  const activeTop = navItems.find((i) => isActive(i.href) && i.children?.length);
  const subNav = activeTop?.children ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      <SoftTopBar />

      <main id="main-content" className="flex-1 px-3 md:px-6 pt-4 pb-36">
        <div className="mx-auto max-w-6xl">
          <ProfileReviewAlert />

          {/* 二级浮岛子导航 */}
          {subNav.length > 0 && (
            <nav className="mb-5 flex flex-wrap gap-2">
              {subNav.map((child) => {
                const childActive = isActive(child.href);
                return (
                  <Link
                    key={child.href}
                    to={child.href}
                    className={cn(
                      'px-4 py-2 rounded-full text-[13px] font-medium transition-all',
                      childActive
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background border border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t(child.labelKey)}
                  </Link>
                );
              })}
            </nav>
          )}

          {/* 内容基座 plinth */}
          <div className={cn('rounded-[28px] bg-card border border-border/70 p-4 md:p-8', PLINTH_SHADOW)}>
            <AnimatePresence>
              <motion.div
                key={pathname}
                initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0, position: 'absolute' }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-8">
            <ConfigurableFooter variant="dashboard" config={footerConfig} />
          </div>
        </div>
      </main>

      {/* 底部浮岛 dock */}
      <SoftDock navItems={navItems} isActive={isActive} />

      {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 顶部浮岛药丸                                                        */
/* ------------------------------------------------------------------ */

function SoftTopBar() {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const tCommon = useTranslations('common');
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations('guided');
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 px-3 md:px-6 pt-3">
      <div
        className={cn(
          'mx-auto max-w-6xl flex items-center gap-3 rounded-full px-4 py-2',
          'bg-card/90 backdrop-blur-xl border border-border/70',
          'shadow-[0_8px_30px_-12px_rgba(0,0,0,0.18)]',
        )}
      >
        <div className="md:hidden">
          <MobileSidebar />
        </div>
        <BrandLogo variant="auto" size="sm" linkTo="/" />
        <span className="flex-1" />

        {guidedEligible && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => setUiMode('guided')}
            aria-label={tGuided('switchGuided')}
            title={tGuided('switchGuided')}
          >
            <Sparkles className="h-4 w-4" />
            <span className="ml-1 hidden lg:inline">{tGuided('switchGuided')}</span>
          </Button>
        )}

        <LanguageSwitcher />
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label={tCommon('toggleTheme')}
        >
          <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-full"
          onClick={() => navigate('/dashboard/messages')}
          aria-label={tCommon('openMessages')}
        >
          <Mail className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>

        <SoftUserMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      </div>
    </header>
  );
}

function SoftUserMenu({ menuOpen, setMenuOpen }: { menuOpen: boolean; setMenuOpen: (v: boolean) => void }) {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { logout, isPending } = useLogout();
  const tAuth = useTranslations('auth');
  const tUsers = useTranslations('users');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const isPrivileged =
    user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger className="rounded-full p-0.5 hover:bg-overlay/60 transition-colors cursor-pointer">
          <Avatar className="h-8 w-8 rounded-full border border-border">
            <AvatarImage src="/avatar.png" />
            <AvatarFallback className="rounded-full text-[11px]">
              {user?.displayName?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium truncate">{user?.displayName || user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <RoleSwitcherMenuItems onSwitchSuccess={() => setMenuOpen(false)} />
          <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
            <PenLine className="h-4 w-4" />
            {tUsers('changeName')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="h-4 w-4" />
            {tUsers('changePassword')}
          </DropdownMenuItem>
          {isPrivileged && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="h-4 w-4" />
                {tAuth('logout.settings')}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} disabled={isPending} className="text-error-text">
            <LogOut className="h-4 w-4" />
            {tAuth('logout.title')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <ChangeNameDialog open={changeNameOpen} onOpenChange={setChangeNameOpen} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 底部浮岛 dock                                                       */
/* ------------------------------------------------------------------ */

function SoftDock({
  navItems,
  isActive,
}: {
  navItems: ReturnType<typeof useNavigation>['navItems'];
  isActive: (href: string) => boolean;
}) {
  const t = useTranslations('nav');
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
      <nav
        className={cn(
          'flex items-end gap-1.5 px-3 py-2 rounded-full',
          'bg-card/95 backdrop-blur-xl border border-border/70',
          DOCK_SHADOW,
        )}
      >
        {navItems.map((item) => {
          const active = isActive(item.href);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              title={t(item.labelKey)}
              className={cn(
                'group relative flex flex-col items-center justify-center rounded-full transition-all duration-200',
                'w-12 h-12 hover:-translate-y-1.5 hover:scale-110',
                active ? 'bg-primary text-primary-foreground shadow-lg' : 'text-muted-foreground hover:text-foreground hover:bg-overlay/60',
              )}
            >
              <Icon className="h-5 w-5" />
              {/* 悬停标签 */}
              <span className="pointer-events-none absolute -top-9 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] text-background opacity-0 group-hover:opacity-100 transition-opacity">
                {t(item.labelKey)}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
